import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
    Calendar,
    Clock,
    MapPin,
    Users,
    DollarSign,
    Image,
    Video,
    Save,
    X,
    Plus,
    Trash2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import FileUpload from '../components/FileUpload';
import ContributingArtistSelector from '../components/ContributingArtistSelector';

const EventForm = () => {
    const navigate = useNavigate();
    const { eventId } = useParams();
    const { user } = useAuth();
    const isEditing = !!eventId;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [uploadType, setUploadType] = useState({
        image: 'url', // 'url' or 'file'
        video: 'url'  // 'url' or 'file'
    });
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        eventType: 'concert',
        startDate: '',
        endDate: '',
        timezone: 'UTC',
        venueName: '',
        address: '',
        city: '',
        state: '',
        country: 'United States',
        capacity: '',
        ticketPrice: '',
        ticketUrl: '',
        ageRequirement: 'all_ages',
        coverImageUrl: '',
        promotionalVideoUrl: '',
        facebookEventUrl: '',
        status: 'draft',
        isFeatured: false,
        isPublic: true,
        tags: []
    });

    const [newTag, setNewTag] = useState('');
    const [contributingArtists, setContributingArtists] = useState([]);

    useEffect(() => {
        if (isEditing) {
            fetchEvent();
        }
    }, [eventId]);

    const fetchEvent = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/events/${eventId}`);
            
            if (response.data.success) {
                const event = response.data.data;
                setFormData({
                    title: event.title || '',
                    description: event.description || '',
                    eventType: event.event_type || 'concert',
                    startDate: formatDateForInput(event.start_date),
                    endDate: event.end_date ? formatDateForInput(event.end_date) : '',
                    timezone: event.timezone || 'UTC',
                    venueName: event.venue_name || '',
                    address: event.address || '',
                    city: event.city || '',
                    state: event.state || '',
                    country: event.country || 'United States',
                    capacity: event.capacity || '',
                    ticketPrice: event.ticket_price || '',
                    ticketUrl: event.ticket_url || '',
                    ageRequirement: event.age_requirement || 'all_ages',
                    coverImageUrl: event.cover_image_url || '',
                    promotionalVideoUrl: event.promotional_video_url || '',
                    facebookEventUrl: event.facebook_event_url || '',
                    status: event.status || 'draft',
                    isFeatured: event.is_featured || false,
                    isPublic: event.is_public !== false,
                    tags: event.tags || []
                });
            }

            try {
                const contributorsResponse = await api.get(`/contributing-artists/event/${eventId}`);
                if (contributorsResponse.data.success) {
                    setContributingArtists(contributorsResponse.data.data);
                }
            } catch (contributorsErr) {
                console.error('Error fetching contributing artists:', contributorsErr);
            }
        } catch (err) {
            console.error('Error fetching event:', err);
            setError('Failed to load event data');
        } finally {
            setLoading(false);
        }
    };

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        // MySQL DATETIME values are timezone-naive wall-clock times. The API
        // serializes them through mysql2's Date object -> JSON.stringify(),
        // which converts to a UTC ISO string. Reading it back out with local
        // getters (not .toISOString(), which converts to UTC *again*) is what
        // correctly reverses that and reproduces the original wall-clock time
        // instead of shifting it by the server's UTC offset.
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`; // YYYY-MM-DDTHH:MM
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const addTag = () => {
        if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const removeTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    // Handle file uploads
    const handleImageUpload = (uploadResult) => {
        setFormData(prev => ({
            ...prev,
            coverImageUrl: uploadResult.url
        }));
    };

    const handleVideoUpload = (uploadResult) => {
        setFormData(prev => ({
            ...prev,
            promotionalVideoUrl: uploadResult.url
        }));
    };

    const handleUploadError = (error) => {
        setError(`Upload failed: ${error}`);
    };

    const toggleUploadType = (mediaType) => {
        setUploadType(prev => ({
            ...prev,
            [mediaType]: prev[mediaType] === 'url' ? 'file' : 'url'
        }));
        // Clear the current value when switching types
        if (mediaType === 'image') {
            setFormData(prev => ({ ...prev, coverImageUrl: '' }));
        } else if (mediaType === 'video') {
            setFormData(prev => ({ ...prev, promotionalVideoUrl: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.title || !formData.startDate) {
            setError('Title and start date are required');
            return;
        }


        // Client-side date validation
        if (formData.endDate) {
            const startDate = new Date(formData.startDate);
            const endDate = new Date(formData.endDate);
            
            if (endDate <= startDate) {
                setError('End date must be after start date');
                return;
            }
        }

        try {
            setLoading(true);
            setError(null);

            // Helper function to send dates as local time (not UTC)
            const formatDateForSubmit = (datetimeValue) => {
                if (!datetimeValue) return null;
                
                // datetime-local always gives us local time in format: "2024-05-14T15:30"
                // We want to send this to the backend as-is (with seconds added)
                // and let the backend treat it as local time
                
                if (datetimeValue.length === 16) { // "YYYY-MM-DDTHH:MM"
                    return datetimeValue + ':00'; // Add seconds: "YYYY-MM-DDTHH:MM:SS"
                }
                
                return datetimeValue;
            };

            // Debug logging
            console.log('EventForm submit - formData.startDate:', formData.startDate);
            console.log('EventForm submit - formData.endDate:', formData.endDate);

            const submitData = {
                ...formData,
                capacity: formData.capacity ? parseInt(formData.capacity) : null,
                ticketPrice: formData.ticketPrice ? parseFloat(formData.ticketPrice) : null,
                // Convert datetime-local format to proper format
                startDate: formatDateForSubmit(formData.startDate),
                endDate: formatDateForSubmit(formData.endDate)
            };

            console.log('EventForm submit - submitData.startDate:', submitData.startDate);
            console.log('EventForm submit - submitData.endDate:', submitData.endDate);

            let response;
            if (isEditing) {
                response = await api.put(`/events/${eventId}`, submitData);
            } else {
                response = await api.post('/events', submitData);
            }

            if (response.data.success) {
                // Save contributing artists (performers/collaborators tagged on this event).
                // Always send on edit (even an empty list) so removing the last one persists;
                // skip the call on a brand-new event with none selected.
                if (isEditing || contributingArtists.length > 0) {
                    const savedEventId = isEditing ? eventId : response.data.data.id;
                    try {
                        const contributingArtistIds = contributingArtists.map(artist => artist.user_id || artist.artist_user_id);
                        await api.post(`/contributing-artists/event/${savedEventId}`, { contributingArtistIds });
                    } catch (contributorsErr) {
                        console.error('Error saving contributing artists:', contributorsErr);
                    }
                }

                // Show success message
                setShowSuccess(true);
                // Navigate after a short delay to show the success message
                setTimeout(() => {
                    navigate('/admin/events');
                }, 1500);
            } else {
                setError(response.data.message || 'Failed to save event');
            }
        } catch (err) {
            console.error('Error saving event:', err);
            
            // Check for specific error types
            if (err.message && err.message.includes('too long')) {
                setError('Event description is too long. Please shorten it and try again.');
            } else if (err.message && err.message.includes('Data too long')) {
                setError('One of the fields contains too much data. Please check your description length.');
            } else if (err.message && err.message.includes('Failed to update event')) {
                setError('Failed to update event. This might be due to the description being too long. Please try shortening it.');
            } else {
                setError('Failed to save event. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const eventTypes = [
        { value: 'concert', label: 'Concert' },
        { value: 'festival', label: 'Festival' },
        { value: 'livestream', label: 'Live Stream' },
        { value: 'workshop', label: 'Workshop' },
        { value: 'meetup', label: 'Meetup' },
        { value: 'release', label: 'Release Event' },
        { value: 'other', label: 'Other' }
    ];

    const ageRequirements = [
        { value: 'all_ages', label: 'All Ages' },
        { value: '18+', label: '18+' },
        { value: '21+', label: '21+' }
    ];

    if (loading && isEditing) {
        return (
            <div className="min-h-screen bg-gray-900 pt-20 pb-12">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="glass-card p-8 text-center">
                        <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3 animate-pulse" />
                        <p className="text-gray-400">Loading event...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 pt-20 pb-12">
            <div className="max-w-4xl mx-auto px-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <Calendar className="w-8 h-8 text-purple-400" />
                        <h1 className="text-4xl font-black gradient-text">
                            {isEditing ? 'Edit Event' : 'Create Event'}
                        </h1>
                    </div>
                    
                    <button
                        onClick={() => navigate('/admin/events')}
                        className="btn-ghost flex items-center gap-2"
                    >
                        <X className="w-5 h-5" />
                        Cancel
                    </button>
                </div>

                {error && (
                    <div className="glass-card p-4 mb-8 border border-red-500/30 bg-red-600/10">
                        <p className="text-red-400">{error}</p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Basic Information */}
                    <div className="glass-card p-6">
                        <h2 className="text-xl font-bold text-white mb-6">Basic Information</h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <label className="block text-gray-300 font-medium mb-2">
                                    Event Title *
                                </label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    placeholder="Enter event title"
                                    required
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-gray-300 font-medium mb-2">
                                    Description
                                    <span className="text-sm text-gray-400 ml-2">
                                        ({formData.description.length.toLocaleString()} characters)
                                    </span>
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows={6}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    placeholder="Describe your event in detail..."
                                />
                                {formData.description.length > 50000 && (
                                    <p className="text-yellow-400 text-sm mt-1">
                                        ⚠️ Very long description. Consider breaking it into sections.
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="block text-gray-300 font-medium mb-2">
                                    Event Type *
                                </label>
                                <select
                                    name="eventType"
                                    value={formData.eventType}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    style={{ color: '#fff' }}
                                    required
                                >
                                    {eventTypes.map(type => (
                                        <option 
                                            key={type.value} 
                                            value={type.value}
                                            style={{ backgroundColor: '#1f2937', color: '#fff' }}
                                        >
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-gray-300 font-medium mb-2">
                                    Age Requirement
                                </label>
                                <select
                                    name="ageRequirement"
                                    value={formData.ageRequirement}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    style={{ color: '#fff' }}
                                >
                                    {ageRequirements.map(age => (
                                        <option 
                                            key={age.value} 
                                            value={age.value}
                                            style={{ backgroundColor: '#1f2937', color: '#fff' }}
                                        >
                                            {age.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Date and Time */}
                    <div className="glass-card p-6">
                        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            Date & Time
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-gray-300 font-medium mb-2">
                                    Start Date & Time *
                                </label>
                                <input
                                    type="datetime-local"
                                    name="startDate"
                                    value={formData.startDate}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-gray-300 font-medium mb-2">
                                    End Date & Time
                                </label>
                                <input
                                    type="datetime-local"
                                    name="endDate"
                                    value={formData.endDate}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Location */}
                    <div className="glass-card p-6">
                        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                            <MapPin className="w-5 h-5" />
                            Location
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-gray-300 font-medium mb-2">
                                    Venue Name
                                </label>
                                <input
                                    type="text"
                                    name="venueName"
                                    value={formData.venueName}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    placeholder="Enter venue name"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-300 font-medium mb-2">
                                    City
                                </label>
                                <input
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    placeholder="Enter city"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-gray-300 font-medium mb-2">
                                    Address
                                </label>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    placeholder="Enter full address"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-300 font-medium mb-2">
                                    State/Province
                                </label>
                                <input
                                    type="text"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    placeholder="Enter state or province"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-300 font-medium mb-2">
                                    Country
                                </label>
                                <input
                                    type="text"
                                    name="country"
                                    value={formData.country}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    placeholder="Enter country"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Tickets & Capacity */}
                    <div className="glass-card p-6">
                        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Tickets & Capacity
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-gray-300 font-medium mb-2">
                                    Capacity
                                </label>
                                <input
                                    type="number"
                                    name="capacity"
                                    value={formData.capacity}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    placeholder="Max attendees"
                                    min="0"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-300 font-medium mb-2">
                                    Ticket Price ($)
                                </label>
                                <input
                                    type="number"
                                    name="ticketPrice"
                                    value={formData.ticketPrice}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    placeholder="0.00"
                                    min="0"
                                    step="0.01"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-300 font-medium mb-2">
                                    Ticket URL
                                </label>
                                <input
                                    type="url"
                                    name="ticketUrl"
                                    value={formData.ticketUrl}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    placeholder="https://tickets.example.com"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Media */}
                    <div className="glass-card p-6">
                        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                            <Image className="w-5 h-5" />
                            Media
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Cover Image */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <label className="block text-gray-300 font-medium">
                                        Cover Image
                                    </label>
                                    <div className="flex bg-gray-700 rounded-lg p-1">
                                        <button
                                            type="button"
                                            onClick={() => toggleUploadType('image')}
                                            className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                                                uploadType.image === 'url'
                                                    ? 'bg-purple-600 text-white'
                                                    : 'text-gray-400 hover:text-gray-300'
                                            }`}
                                        >
                                            URL
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => toggleUploadType('image')}
                                            className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                                                uploadType.image === 'file'
                                                    ? 'bg-purple-600 text-white'
                                                    : 'text-gray-400 hover:text-gray-300'
                                            }`}
                                        >
                                            Upload
                                        </button>
                                    </div>
                                </div>
                                
                                {uploadType.image === 'url' ? (
                                    <input
                                        type="url"
                                        name="coverImageUrl"
                                        value={formData.coverImageUrl}
                                        onChange={handleInputChange}
                                        className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                        placeholder="https://example.com/image.jpg"
                                    />
                                ) : (
                                    <FileUpload
                                        type="image"
                                        onUpload={handleImageUpload}
                                        onError={handleUploadError}
                                        maxSizeMB={10}
                                        className="h-32"
                                        compact
                                    >
                                        Upload cover image
                                    </FileUpload>
                                )}
                            </div>

                            {/* Promotional Video */}
                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <label className="block text-gray-300 font-medium">
                                        Promotional Video
                                    </label>
                                    <div className="flex bg-gray-700 rounded-lg p-1">
                                        <button
                                            type="button"
                                            onClick={() => toggleUploadType('video')}
                                            className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                                                uploadType.video === 'url'
                                                    ? 'bg-purple-600 text-white'
                                                    : 'text-gray-400 hover:text-gray-300'
                                            }`}
                                        >
                                            URL
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => toggleUploadType('video')}
                                            className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                                                uploadType.video === 'file'
                                                    ? 'bg-purple-600 text-white'
                                                    : 'text-gray-400 hover:text-gray-300'
                                            }`}
                                        >
                                            Upload
                                        </button>
                                    </div>
                                </div>
                                
                                {uploadType.video === 'url' ? (
                                    <input
                                        type="url"
                                        name="promotionalVideoUrl"
                                        value={formData.promotionalVideoUrl}
                                        onChange={handleInputChange}
                                        className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                        placeholder="https://example.com/video.mp4"
                                    />
                                ) : (
                                    <FileUpload
                                        type="video"
                                        onUpload={handleVideoUpload}
                                        onError={handleUploadError}
                                        maxSizeMB={100}
                                        className="h-32"
                                        compact
                                    >
                                        Upload promotional video
                                    </FileUpload>
                                )}
                            </div>
                        </div>

                        <div className="mt-6">
                            <label className="block text-gray-300 font-medium mb-2">
                                Facebook Event Link
                            </label>
                            <input
                                type="url"
                                name="facebookEventUrl"
                                value={formData.facebookEventUrl}
                                onChange={handleInputChange}
                                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                placeholder="https://www.facebook.com/events/..."
                            />
                            <p className="text-gray-500 text-sm mt-2">
                                Shown as a preview card on the event page, linking back to the Facebook event.
                            </p>
                        </div>
                    </div>

                    {/* Tags */}
                    <div className="glass-card p-6">
                        <h2 className="text-xl font-bold text-white mb-6">Tags</h2>
                        
                        <div className="flex flex-wrap gap-2 mb-4">
                            {formData.tags.map((tag, index) => (
                                <span key={index} className="flex items-center gap-2 bg-purple-600/20 text-purple-300 px-3 py-1 rounded-full text-sm">
                                    {tag}
                                    <button
                                        type="button"
                                        onClick={() => removeTag(tag)}
                                        className="text-purple-300 hover:text-red-400"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </span>
                            ))}
                        </div>

                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-purple-500"
                                placeholder="Add a tag..."
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                            />
                            <button
                                type="button"
                                onClick={addTag}
                                className="btn-secondary flex items-center gap-2"
                            >
                                <Plus className="w-4 h-4" />
                                Add
                            </button>
                        </div>
                    </div>

                    {/* Contributing Artists */}
                    <div className="glass-card p-6">
                        <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Contributing Artists
                        </h2>
                        <ContributingArtistSelector
                            selectedArtists={contributingArtists}
                            onArtistsChange={setContributingArtists}
                            placeholder="Search for performers, hosts, or collaborators on this event..."
                            maxArtists={20}
                            disabled={loading}
                        />
                    </div>

                    {/* Settings */}
                    <div className="glass-card p-6">
                        <h2 className="text-xl font-bold text-white mb-6">Event Settings</h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label className="block text-gray-300 font-medium mb-2">
                                    Status
                                </label>
                                <select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500"
                                    style={{ color: '#fff' }}
                                >
                                    <option value="draft" style={{ backgroundColor: '#1f2937', color: '#fff' }}>Draft</option>
                                    <option value="published" style={{ backgroundColor: '#1f2937', color: '#fff' }}>Published</option>
                                    <option value="cancelled" style={{ backgroundColor: '#1f2937', color: '#fff' }}>Cancelled</option>
                                    <option value="postponed" style={{ backgroundColor: '#1f2937', color: '#fff' }}>Postponed</option>
                                </select>
                            </div>

                            <div className="flex items-center">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="isFeatured"
                                        checked={formData.isFeatured}
                                        onChange={handleInputChange}
                                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 focus:ring-2"
                                    />
                                    <span className="text-gray-300">Featured Event</span>
                                </label>
                            </div>

                            <div className="flex items-center">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="isPublic"
                                        checked={formData.isPublic}
                                        onChange={handleInputChange}
                                        className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500 focus:ring-2"
                                    />
                                    <span className="text-gray-300">Public Event</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-4">
                        <button
                            type="button"
                            onClick={() => navigate('/admin/events')}
                            className="flex-1 btn-ghost"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="flex-1 btn-primary flex items-center justify-center gap-2"
                            disabled={loading}
                        >
                            {loading ? (
                                <div className="loading-spinner w-5 h-5"></div>
                            ) : (
                                <Save className="w-5 h-5" />
                            )}
                            {loading ? 'Saving...' : (isEditing ? 'Update Event' : 'Create Event')}
                        </button>
                    </div>
                </form>
            </div>

            {/* Success Popup */}
            {showSuccess && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="glass-card p-8 text-center animate-fade-in">
                        <div className="text-6xl mb-4">🎉</div>
                        <h2 className="text-2xl font-bold gradient-text mb-2">
                            Event {isEditing ? 'Updated' : 'Created'} Successfully!
                        </h2>
                        <p className="text-gray-400 mb-4">
                            Your event "{formData.title}" has been {isEditing ? 'updated' : 'saved'}.
                        </p>
                        <div className="loading-spinner w-6 h-6 mx-auto mb-2"></div>
                        <p className="text-sm text-gray-500">Redirecting to event management...</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EventForm;